type MessageType = string | number
type GoogleMessage = {
  proceed: (allow: boolean, messageTypes?: MessageType[]) => void
}
type MessagingFunction = (message: GoogleMessage) => void | Promise<void>
type PrivacyDecision = { allow: boolean; types?: MessageType[] }

declare global {
  interface Window {
    googlefc?: {
      controlledMessagingFunction?: MessagingFunction
      MessageTypeEnum?: { OFFERWALL?: MessageType }
    }
  }
}

let installedFunction: MessagingFunction | undefined
const PRIOR_HOOK_TIMEOUT_MS = 10_000

/**
 * Keep native Offerwall off even when banner tags load Google's messaging SDK.
 * This only installs the official messaging hook; it never requests an ad/tag.
 */
export function suppressNativeGoogleOfferwall(): void {
  if (typeof window === 'undefined') return
  const fc = (window.googlefc = window.googlefc || {})
  if (installedFunction && fc.controlledMessagingFunction === installedFunction)
    return
  const previous = fc.controlledMessagingFunction
  installedFunction = (message) => {
    let settled = false
    let priorDecision: PrivacyDecision | null = previous
      ? null
      : { allow: true }
    let retryTimer: ReturnType<typeof setTimeout> | undefined
    const finish = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      clearTimeout(retryTimer)
      const prior = priorDecision || { allow: true }
      const offerwallType = window.googlefc?.MessageTypeEnum?.OFFERWALL
      if (
        (!prior.allow && !prior.types?.length) ||
        offerwallType === undefined
      ) {
        // An older/failed SDK without the supported enum must not block CMP.
        message.proceed(prior.allow, prior.types)
        return
      }
      const blocked = prior.allow
        ? [offerwallType]
        : [...new Set([...(prior.types || []), offerwallType])]
      message.proceed(false, blocked)
    }
    const flush = () => {
      if (settled || !priorDecision) return
      if (
        (!priorDecision.allow && !priorDecision.types?.length) ||
        window.googlefc?.MessageTypeEnum?.OFFERWALL !== undefined
      ) {
        finish()
      } else if (retryTimer === undefined) {
        // SDK enums normally exist before this hook is invoked. Allow a brief
        // initialization race without suppressing every privacy message.
        retryTimer = setTimeout(() => {
          retryTimer = undefined
          flush()
        }, 50)
      }
    }
    const timer = setTimeout(finish, PRIOR_HOOK_TIMEOUT_MS)
    const fallback = () => {
      if (settled || priorDecision) return
      priorDecision = { allow: true }
      flush()
    }
    if (previous) {
      try {
        const result = previous({
          ...message,
          proceed: (allow, types) => {
            if (settled || priorDecision) return
            priorDecision = { allow, types }
            flush()
          },
        })
        void Promise.resolve(result).catch(fallback)
      } catch {
        fallback()
      }
    }
    flush()
  }
  fc.controlledMessagingFunction = installedFunction
}
